import React, { useEffect, useRef, useState } from "react";
import { Nav, Tab } from "react-bootstrap";
import { useForm } from "react-hook-form";
import CreditPaymentForm from "../../auth/payment/CreditPaymentForm";
import AddAchPaymentForm from "./AddAchPaymentForm";
import { CapitalizeString } from "../../auth/payment/RegisterPayment";
import SikkaInput from "../../common/SikkaInput";
import { Button, Col, ModalFooter, Row } from "reactstrap";
import creditCard from "../../../assets/img/icons/icon-payment-methods.png";

const AddPayment = ({
  setFormValue,
  formValue,
  isApiCalled,
  handlePaymentFormSubmit,
  setIsActive,
  defaultActiveKey,
  setDefaultActiveKey,

  // ✨ Flexible Props
  showBillingInfo = true,
  showVerificationNotice = true,
  addButtonText = "Add",
  cancelButtonText = "Cancel",
  showCancelButton = true,
  showAddButton = true,
  tabNames = { credit: "Credit Card", ach: "ACH" },

  // ✅ New: Customizable verification amount
  verificationAmount = "0.01",
  customButton = "primary",
  submitButtonCustomClass=""
}) => {
  const [currentFormValue, setCurrentFormValue] = useState(formValue);

  const {
    register,
    handleSubmit,
    setValue,
    errors,
    watch,
    clearError,
    setError,
    reset,
    unregister,
  } = useForm({
    defaultValues: formValue, // ✅ set initial values
  });

  const creditCardRef = useRef();
  const achCardRef = useRef();

  const liveWatch = watch();
  const liveWatchString = JSON.stringify(liveWatch);

  useEffect(() => {
    const merged = { ...currentFormValue, ...liveWatch };
    setCurrentFormValue(merged);
    setFormValue(merged);
  }, [liveWatchString]);

  useEffect(() => {
    const activePrefix = defaultActiveKey === "credit" ? "credit" : "ach";
    const inactivePrefix = defaultActiveKey === "credit" ? "ach" : "credit";

    Object.keys(currentFormValue)
      .filter((item) => item.includes(activePrefix))
      .forEach((item) => setValue(item, currentFormValue[item]));

    Object.keys(currentFormValue)
      .filter((item) => item.includes(inactivePrefix))
      .forEach((item) => unregister(item));
  }, [defaultActiveKey]);

  const handleTrimAndSet = (e) => {
    const value = e.target.value.trim();
    const name = e.target.name;

    if (!value) {
      setError(
        name,
        "custom",
        `${CapitalizeString(name)} should not be empty or whitespace.`
      );
    } else {
      clearError(name);
      setValue(name, value);
    }
  };

  const renderBillingInfo = () => (
    <>
      <Row className="mt-1 mb-1">
        <Col>
          <h6>Billing Information</h6>
        </Col>
      </Row>
      <Row>
        <Col>
          <SikkaInput
            onBlur={handleTrimAndSet}
            label="First Name*"
            placeholder="First Name"
            id="first_name"
            name="first_name"
            innerRef={register({ required: "First Name is required" })}
            errors={errors}
          />
        </Col>
        <Col>
          <SikkaInput
            onBlur={handleTrimAndSet}
            label="Last Name*"
            placeholder="Last Name"
            id="last_name"
            name="last_name"
            innerRef={register({ required: "Last Name is required" })}
            errors={errors}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <SikkaInput
            onBlur={handleTrimAndSet}
            label="Address*"
            placeholder="Address"
            id="address"
            name="address"
            innerRef={register({ required: "Address is required" })}
            errors={errors}
          />
        </Col>
        <Col>
          <SikkaInput
            onBlur={handleTrimAndSet}
            label="City*"
            placeholder="City"
            id="city"
            name="city"
            innerRef={register({ required: "City is required" })}
            errors={errors}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <SikkaInput
            onBlur={handleTrimAndSet}
            label="State*"
            placeholder="State"
            id="state"
            name="state"
            innerRef={register({ required: "State is required" })}
            errors={errors}
          />
        </Col>
        <Col>
          <SikkaInput
            onBlur={handleTrimAndSet}
            label="Zip*"
            placeholder="Zip"
            id="zip"
            name="zip"
            innerRef={register({ required: "Zip is required" })}
            errors={errors}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <SikkaInput
            onBlur={handleTrimAndSet}
            label="Email Id*"
            placeholder="Email ID"
            id="email_id"
            name="email_id"
            innerRef={register({
              required: "Email is required",
              pattern: {
                value: /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                message: "Email must be valid",
              },
            })}
            errors={errors}
          />
        </Col>
      </Row>
    </>
  );

  return (
    <>
      <Tab.Container
        defaultActiveKey={defaultActiveKey}
        onSelect={(selectedValue) => setDefaultActiveKey?.(selectedValue)}
      >
        <Nav variant="tabs">
          <Nav.Item>
            <Nav.Link
              eventKey="credit"
              className="fs--1 px-2 px-md-3 cursor-pointer outline-none"
            >
              {tabNames.credit}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey="ach"
              className="fs--1 px-2 px-md-3 cursor-pointer outline-none"
            >
              {tabNames.ach}
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Row className="mb-2 mt-2">
          <Col className="d-flex justify-content-center">
            <img
              src={creditCard}
              height="20"
              alt="payment-methods"
              width="auto"
            />
          </Col>
        </Row>

        <div className="payment-card-containers">
          <Tab.Content>
            <Tab.Pane className="mt-2 mb-1" eventKey="credit">
              {defaultActiveKey === "credit" && (
                <CreditPaymentForm
                  register={register}
                  errors={errors}
                  watch={watch}
                  reset={reset}
                  setValue={setValue}
                  formRef={creditCardRef}
                  form_name={defaultActiveKey}
                  setError={setError}
                  clearError={clearError}
                  currentFormValue={currentFormValue}
                />
              )}
            </Tab.Pane>

            <Tab.Pane className="mt-2 mb-1" eventKey="ach">
              {defaultActiveKey === "ach" && (
                <AddAchPaymentForm
                  defaultActiveKey={defaultActiveKey}
                  setError={setError}
                  setValue={setValue}
                  errors={errors}
                  register={register}
                  clearError={clearError}
                  formRef={achCardRef}
                />
              )}
            </Tab.Pane>
          </Tab.Content>

          {showBillingInfo && renderBillingInfo()}

          {showVerificationNotice && (
            <div
              className="payment-notice bg-200 p-2 mt-2 mb-2"
              style={{ fontSize: "1.3em" }}
            >
              <small>
                <b className="text-danger">Verification Notice :</b>
              </small>
              <small>
                <p>
                  To ensure the security of your payment method, we will process
                  a temporary charge of ${verificationAmount} on your{" "}
                  {defaultActiveKey === "ach" ? "account" : "Credit card"}.
                </p>
                <p>
                  This amount will be immediately refunded to your account after
                  verification.
                </p>
              </small>
            </div>
          )}
        </div>
      </Tab.Container>

      {/* <ModalFooter> */}
      <Row className="">
        <Col className="d-flex justify-content-end">
          {showAddButton && (
            <Button
              className={`${submitButtonCustomClass ? submitButtonCustomClass : ' ml-auto text-brand'}`}
              onClick={handleSubmit(handlePaymentFormSubmit)}
              size="sm"
              disabled={isApiCalled}
            >
              {addButtonText}
            </Button>
          )}
          {showCancelButton && (
            <Button
              color="ml-2 outline-secondary"
              size="sm"
              onClick={() => setIsActive(false)}
            >
              {cancelButtonText}
            </Button>
          )}
        </Col>
      </Row>

      {/* </ModalFooter> */}
    </>
  );
};

export default AddPayment;
